import type { StepStatus } from '@/api/onboarding';

/**
 * Starter questions for the empty panel.
 *
 * A blank text box is the worst possible first-run affordance: it asks the user
 * to already know what Gateco can do. These are keyed off where the user is and
 * what they have not finished setting up.
 */
const BY_ROUTE: Record<string, string[]> = {
  '/connectors': [
    'What do I put in expected_dimension?',
    'What does metadata_resolution_mode mean, and which should I pick?',
    'My connector says Not Ready. What is missing?',
  ],
  '/policy-studio': [
    'Why does my policy condition not match anything?',
    'What is the difference between allow and deny policies?',
    'Which policy template should I start with?',
  ],
  '/directory': [
    'How do I add a user without connecting an identity provider?',
    'How do groups get used in policies?',
  ],
  '/audit-log': ['Why was this retrieval denied?', 'What do the denial reasons mean?'],
  '/access-simulator': ['How do I test what a principal can see?'],
  '/answer-synthesis': [
    'Why did I get insufficient_context?',
    'Do I need my own OpenAI key?',
  ],
  '/usage-billing': ['What counts as a secured retrieval?', 'What am I paying for?'],
  '/data-catalog': ['How do I register my existing vectors?', 'How does chunking work?'],
  '/settings/api-keys': ['Which scope does the quickstart need?'],
};

const BY_STEP: Record<string, string> = {
  connect_vector_db: 'How do I connect my vector database?',
  configure_search: 'What goes in the search configuration?',
  register_resources: 'How do I register vectors that are already in my database?',
  connect_idp: 'How do I add users or connect an identity provider?',
  create_policy: 'How do I write my first policy?',
  first_retrieval: 'How do I run my first secured retrieval?',
};

const FALLBACK = [
  'What is Gateco and how does it work?',
  'What are the steps to get set up?',
  'Why are my retrievals denied?',
];

export function suggestedQuestions(
  route: string,
  nextStep?: StepStatus | null
): string[] {
  const out: string[] = [];
  if (nextStep && BY_STEP[nextStep.id]) out.push(BY_STEP[nextStep.id]);
  for (const q of BY_ROUTE[route] ?? []) {
    if (!out.includes(q)) out.push(q);
  }
  for (const q of FALLBACK) {
    if (out.length >= 3) break;
    if (!out.includes(q)) out.push(q);
  }
  return out.slice(0, 3);
}
